import React, { useEffect, useState, Suspense, lazy, useMemo } from "react";
import Navbar from "./navbar";
import Footer from "./Footer";
import Aos from "aos";
import "aos/dist/aos.css";
import { NavLink } from "react-router-dom";
import Number from "./Number";
import axios from "axios";
import { Helmet } from "react-helmet";
import useIsMobile from "./hooks/useIsMobile";
import Hero from "./Components/Hero/Hero";

// Lazy load non-critical components
const ProductCarousel = lazy(() =>
  import("./Components/ProductCarousel").catch(() => ({
    default: () => <div>Unable to load products</div>,
  }))
);


// Lightweight fallback for lazy-loaded sections
const LoadingFallback = () => <div style={{ minHeight: "300px" }}></div>;

const Home = () => {
  const [bannerData, setBannerData] = useState([]);
  const [restData, setRestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restLoading, setRestLoading] = useState(false);
  const [error, setError] = useState(null);
  const isMobile = useIsMobile();

  // 🧠 Pre-memoize API URLs to avoid re-renders
  const API_URL = useMemo(
    () => "https://triverseadvertising.com/redcow/api/home.php",
    []
  );


  useEffect(() => {
    let active = true;

    Aos.init({ duration: 1200, once: true });

    const fetchBannerData = async () => {
      try {
        const { data } = await axios.get(API_URL, { timeout: 6000 });
        if (active && data?.banners) {
          setBannerData(data.banners);
          setError(null);
        }
      } catch (err) {
        console.error("Banner fetch failed:", err);
        setError("Unable to load banners");
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchBannerData();
    return () => (active = false);
  }, [API_URL]);

  /** ------------------------------------------------
   *  STEP 2: Fetch rest data (deferred till idle)
   *  ------------------------------------------------ */
  useEffect(() => {
    let active = true;
    const fetchRestData = async () => {
      setRestLoading(true);
      try {
        const { data } = await axios.get(API_URL, { timeout: 8000 });
        if (active && data) setRestData(data);
      } catch (err) {
        console.error("Rest data fetch failed:", err);
      } finally {
        if (active) setRestLoading(false);
      }
    };

    // Use requestIdleCallback for better thread timing
    if ("requestIdleCallback" in window) {
      requestIdleCallback(fetchRestData, { timeout: 3000 });
    } else {
      window.addEventListener("load", fetchRestData, { once: true });
    }

    return () => (active = false);
  }, [API_URL]);

  /** ------------------------------------------------
   *  STEP 3: Render logic
   *  ------------------------------------------------ */
  if (loading)
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fff",
        }}
      >
        <div className="spinner-border text-danger" role="status" />
      </div>
    );

  if (error) return <div className="text-center py-5">{error}</div>;

  if (!bannerData?.length)
    return <div className="text-center py-5">No data available</div>;

  return (
    <>
      <Navbar />
      <Helmet>
        <title>Red Cow Dairy | Freshness You Can Trust</title>
        <meta
          name="description"
          content="Red Cow Dairy brings the goodness of pure, farm-fresh milk and dairy products to your home."
        />
        {/* ✅ Preload first banner image to boost LCP */}
        {bannerData[0] && (
          <link
            rel="preload"
            as="image"
            href={isMobile ? bannerData[0]?.mobile_img : bannerData[0]?.img}
            media={isMobile ? "(max-width:768px)" : "(min-width:769px)"}
          />
        )}
      </Helmet>

      {/* ------------------------------------------------
       *  HERO SECTION
       * ------------------------------------------------ */}
      <Hero bannerData={bannerData} isMobile={isMobile} />
      {/* ------------------------------------------------
       *  NON-CRITICAL CONTENT (DEFERRED)
       * ------------------------------------------------ */}
      {restData ? (
        <>
          <Suspense fallback={<LoadingFallback />}>
            <ProductCarousel products={restData.products} />
          </Suspense>

          <Suspense fallback={<LoadingFallback />}>
            <Number numbers={restData} />
          </Suspense>

          {/* Sweet Banner */}
          {restData.about?.quote && (
            <section className="sweetbanner">
              <div className="container text-center">
                <h2
                  data-aos="fade-up"
                  dangerouslySetInnerHTML={{
                    __html: restData.about.quote,
                  }}
                />
                <div className="d-flex justify-content-center flex-row gap-5  mx-auto">
                  <a
                    href="/icecream"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="knowmore firstbtn"
                  >
                    <span>Know more</span>
                  </a>
                  <a
                    href="/icecream/parlour-locator/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="knowmore secondbtn"
                  >
                    <span>Find a Parlour</span>
                  </a>
                </div>
              </div>
            </section>
          )}

          {/* About Section */}
          {restData.about && (
            <section className="aboutus">
              <div className="container">
                <div className="row">
                  <div className="web-container">
                    <div className="heading">
                      <h2 data-aos="fade-up">{restData.about.heading}</h2>

                      <p data-aos="fade-up">{restData.about.content}</p>
                    </div>
                    <NavLink to="/our-story">
                      <div className="probtn btncenter col-12" data-aos="fade-up">
                        <span className="btn-3">
                          <span>Know more</span>
                        </span>
                      </div>
                    </NavLink>
                  </div>
                </div>
              </div>
            </section>
          )}
        </>
      ) : (
        restLoading && <LoadingFallback />
      )}

      <Footer />
    </>
  );
};

export default Home;
